import type {
  GroupType,
  GroupTreeNodeRequest,
  VariantComboGroupView,
  VariantComboItemView,
  UpsertVariantComboGroupRequest,
} from '../../../../../services/variants-combos/variants-combos.service';

// Architectural summary: Pure transforms + tree helpers for the Options Builder drawer (Rule 32).

export type ComboMode = 'upTo' | 'exactly' | 'range' | 'unlimited';

export const UNLIMITED_PICK_LIMIT = 999;

export type UiItem = {
  id: string;
  backendId?: number;
  name: string;
  isDefault: boolean;
  price: number; // the "after-limit" price X (charged once the group's free quota is exhausted, or flat if no quota)
  priceWithin: number; // the "within-limit" price Y for the first `freeLimit` picks (0 = free). Ignored when no quota.
  kds: boolean | null;
  maxPick: number | null; // per-item selection cap (null = unlimited up to group pickLimit)
  printerIds: number[];
  childGroups: UiGroup[];
};

export type UiGroup = {
  id: string;
  backendId?: number;
  name: string;
  type: GroupType;
  required: boolean; // variant only
  mode: ComboMode; // combo only
  nMin: number;
  nMax: number;
  freeOn: boolean;
  freeLimit: number;
  items: UiItem[];
};

let _uid = 0;
export const uid = (prefix: string): string => `${prefix}_${(_uid += 1)}`;

// New items inherit the father product's KDS flag + printer routing; both stay manually editable.
export type NewItemDefaults = { kds?: boolean | null; printerIds?: number[] };

// Seed new items from the father product, but only with printers that are currently active —
// an inactive/offline id can't render in the selector and the backend rejects it on save.
export function resolveInheritedItemDefaults(
  kds: boolean,
  printerIds: number[],
  activePrinterIds: Iterable<number>,
): NewItemDefaults {
  const active = new Set(activePrinterIds);
  return { kds, printerIds: printerIds.filter((id) => active.has(id)) };
}

export function mkItem(name = '', defaults: NewItemDefaults = {}): UiItem {
  return {
    id: uid('it'),
    name,
    isDefault: false,
    price: 0,
    priceWithin: 0,
    kds: defaults.kds ?? null,
    maxPick: null,
    printerIds: defaults.printerIds ? [...defaults.printerIds] : [],
    childGroups: [],
  };
}

export function mkGroup(overrides: Partial<UiGroup> = {}): UiGroup {
  return {
    id: uid('g'),
    name: '',
    type: 'variant',
    required: false,
    mode: 'upTo',
    nMin: 1,
    nMax: 1,
    freeOn: false,
    freeLimit: 1,
    items: [],
    ...overrides,
  };
}


export function pickLimits(g: UiGroup): { min: number; max: number } {
  if (g.mode === 'unlimited') return { min: 0, max: UNLIMITED_PICK_LIMIT };
  if (g.mode === 'upTo') return { min: 0, max: g.nMax };
  if (g.mode === 'exactly') return { min: g.nMax, max: g.nMax };
  return { min: g.nMin, max: g.nMax }; // range
}

export type RuleParts = { lead: string; strong: string; tail: string; free: string | null };

export function firstNPhrase(n: number): string {
  return n === 1 ? 'הפריט הראשון' : `${n} הפריטים הראשונים`;
}

export function freeQuotaLabel(n: number): string {
  return `${firstNPhrase(n)} חינם`;
}

export function tierQuotaLabel(n: number): string {
  return `${firstNPhrase(n)} במחיר מוזל`;
}

export function quotaNote(g: UiGroup): string {
  const allFree = g.items.every((it) => (it.priceWithin ?? 0) === 0);
  return allFree ? freeQuotaLabel(g.freeLimit) : tierQuotaLabel(g.freeLimit);
}

export function genRule(g: UiGroup): RuleParts {
  const { min, max } = pickLimits(g);
  let strong: string;
  if (g.mode === 'unlimited') strong = 'ללא הגבלה';
  else if (g.mode === 'exactly') strong = max === 1 ? 'פריט אחד' : `בדיוק ${max} פריטים`;
  else if (g.mode === 'upTo') strong = max === 1 ? 'פריט אחד' : `עד ${max} פריטים`;
  else strong = `לפחות ${min} ועד ${max} פריטים`;
  const tail = g.mode !== 'unlimited' && max === 1 ? (min >= 1 ? ' · חובה' : ' · לא חובה') : '';
  return { lead: 'הלקוח בוחר ', strong, tail, free: g.freeOn && max > 1 ? quotaNote(g) : null };
}


export type GroupErrors = { name?: string; items?: string; rule?: string; free?: string; defaults?: string };

export function groupErrors(g: UiGroup): GroupErrors {
  const errs: GroupErrors = {};
  if (!g.name.trim()) errs.name = 'יש להזין שם לקבוצה';
  if (g.items.length === 0) errs.items = 'יש להוסיף לפחות פריט אחד';
  else if (g.items.some((it) => !it.name.trim())) errs.items = 'יש להזין שם לכל פריט';
  const { min, max } = pickLimits(g);
  if (max < 1) errs.rule = 'המקסימום חייב להיות לפחות 1';
  else if (g.mode === 'range' && min > max) errs.rule = 'המינימום גדול מהמקסימום';
  if (g.freeOn && max > 1 && g.freeLimit >= max) errs.free = 'מכסת החינם חייבת להיות קטנה מהמקסימום';
  if (max === 1 && g.items.filter((it) => it.isDefault).length > 1) errs.defaults = 'ניתן לסמן ברירת מחדל אחת בלבד כשבוחרים פריט אחד';
  return errs;
}

export type InvalidGroup = { id: string; name: string; errs: GroupErrors };

export function allErrors(groups: UiGroup[], bag: InvalidGroup[] = []): InvalidGroup[] {
  for (const g of groups) {
    const e = groupErrors(g);
    if (Object.keys(e).length) bag.push({ id: g.id, name: g.name, errs: e });
    for (const it of g.items) allErrors(it.childGroups, bag);
  }
  return bag;
}



type GroupPatch = Partial<UiGroup> | ((g: UiGroup) => Partial<UiGroup>);
type ItemPatch = Partial<UiItem> | ((it: UiItem) => Partial<UiItem>);

export function mapGroup(groups: UiGroup[], id: string, patch: GroupPatch): UiGroup[] {
  return groups.map((g0) => {
    const g = g0.id === id ? { ...g0, ...(typeof patch === 'function' ? patch(g0) : patch) } : g0;
    return { ...g, items: g.items.map((it) => (it.childGroups.length ? { ...it, childGroups: mapGroup(it.childGroups, id, patch) } : it)) };
  });
}

export function mapItem(groups: UiGroup[], id: string, patch: ItemPatch): UiGroup[] {
  return groups.map((g) => ({
    ...g,
    items: g.items.map((it0) => {
      const it = it0.id === id ? { ...it0, ...(typeof patch === 'function' ? patch(it0) : patch) } : it0;
      return { ...it, childGroups: it.childGroups.length ? mapItem(it.childGroups, id, patch) : it.childGroups };
    }),
  }));
}

export function removeGroupNode(groups: UiGroup[], id: string): UiGroup[] {
  return groups
    .filter((g) => g.id !== id)
    .map((g) => ({ ...g, items: g.items.map((it) => (it.childGroups.length ? { ...it, childGroups: removeGroupNode(it.childGroups, id) } : it)) }));
}

export function removeItemNode(groups: UiGroup[], id: string): UiGroup[] {
  return groups.map((g) => ({
    ...g,
    items: g.items.filter((it) => it.id !== id).map((it) => (it.childGroups.length ? { ...it, childGroups: removeItemNode(it.childGroups, id) } : it)),
  }));
}

// Architectural summary: Move an item within its group (recurses); sortOrder renumbered at save.
export function reorderItemsInGroup(groups: UiGroup[], groupId: string, activeItemId: string, overItemId: string): UiGroup[] {
  return groups.map((g0) => {
    let g = g0;
    if (g.id === groupId) {
      const from = g.items.findIndex((it) => it.id === activeItemId);
      const to = g.items.findIndex((it) => it.id === overItemId);
      if (from >= 0 && to >= 0 && from !== to) {
        const items = [...g.items];
        const [moved] = items.splice(from, 1);
        items.splice(to, 0, moved);
        g = { ...g, items };
      }
    }
    return {
      ...g,
      items: g.items.map((it) => (it.childGroups.length ? { ...it, childGroups: reorderItemsInGroup(it.childGroups, groupId, activeItemId, overItemId) } : it)),
    };
  });
}

function reorderGroupList(list: UiGroup[], activeGroupId: string, overGroupId: string): UiGroup[] {
  const from = list.findIndex((g) => g.id === activeGroupId);
  const to = list.findIndex((g) => g.id === overGroupId);
  if (from < 0 || to < 0 || from === to) {
    return list;
  }
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

// Architectural summary: Move a group within one sibling list (root or nested under one parent item).
export function reorderGroupsInParent(
  groups: UiGroup[],
  parentItemId: string | null,
  activeGroupId: string,
  overGroupId: string,
): UiGroup[] {
  if (parentItemId === null) {
    return reorderGroupList(groups, activeGroupId, overGroupId);
  }
  return groups.map((g) => ({
    ...g,
    items: g.items.map((it) => {
      if (it.id === parentItemId) {
        return {
          ...it,
          childGroups: reorderGroupList(it.childGroups, activeGroupId, overGroupId),
        };
      }
      return it.childGroups.length
        ? {
            ...it,
            childGroups: reorderGroupsInParent(it.childGroups, parentItemId, activeGroupId, overGroupId),
          }
        : it;
    }),
  }));
}

export function addChildGroupTo(groups: UiGroup[], itemId: string, group: UiGroup): UiGroup[] {
  return mapItem(groups, itemId, (it) => ({ childGroups: [...it.childGroups, group] }));
}

export function toggleDefaultIn(groups: UiGroup[], itemId: string): UiGroup[] {
  return groups.map((g) => {
    const target = g.items.find((it) => it.id === itemId);
    if (target) {
      const willBeDefault = !target.isDefault;
      const radio = pickLimits(g).max === 1 && willBeDefault;
      return {
        ...g,
        items: g.items.map((it) =>
          it.id === itemId ? { ...it, isDefault: willBeDefault } : radio && it.isDefault ? { ...it, isDefault: false } : it,
        ),
      };
    }
    return { ...g, items: g.items.map((it) => (it.childGroups.length ? { ...it, childGroups: toggleDefaultIn(it.childGroups, itemId) } : it)) };
  });
}

export function setGroupType(groups: UiGroup[], id: string, type: GroupType): UiGroup[] {
  return mapGroup(groups, id, () => ({ type }));
}

// Keep one default per pick-1 group (first by order, uncheck the rest); recurses into children.
export function collapseSinglePickDefaults(groups: UiGroup[]): UiGroup[] {
  return groups.map((g) => {
    const single = pickLimits(g).max === 1;
    let kept = false;
    return {
      ...g,
      items: g.items.map((it) => {
        const isDefault = single && it.isDefault ? (kept ? false : ((kept = true), true)) : it.isDefault;
        const childGroups = it.childGroups.length ? collapseSinglePickDefaults(it.childGroups) : it.childGroups;
        return { ...it, isDefault, childGroups };
      }),
    };
  });
}

export function setGroupNMax(groups: UiGroup[], id: string, nMax: number): UiGroup[] {
  return collapseSinglePickDefaults(mapGroup(groups, id, { nMax }));
}

export function setGroupMode(groups: UiGroup[], id: string, mode: ComboMode): UiGroup[] {
  const next = mapGroup(groups, id, (g) => {
    const patch: Partial<UiGroup> = { mode };
    if (mode === 'unlimited') {
      return patch;
    }
    // Leaving "no limit": its 999 sentinel must not become the new count — fall back to 1.
    const base = g.nMax >= UNLIMITED_PICK_LIMIT ? 1 : Math.max(1, g.nMax);
    if (mode === 'range') {
      patch.nMin = Math.min(g.nMin || 1, base);
      patch.nMax = Math.max(base, (patch.nMin || 1) + 1);
    } else {
      patch.nMax = base;
    }
    return patch;
  });
  return collapseSinglePickDefaults(next);
}


export function fromModel(groups: VariantComboGroupView[]): UiGroup[] {
  return groups.map((g) => fromModelGroup(g));
}

// Backfill the father's kds+printers into never-configured combo items (kds null AND no printers);
// draft-only + editable, saved normally; explicit items and variant items stay untouched.
export function applyInheritedDefaults(groups: UiGroup[], defaults: NewItemDefaults): UiGroup[] {
  return groups.map((g) => ({
    ...g,
    items: g.items.map((it) => {
      const unset = it.kds === null && it.printerIds.length === 0;
      const seeded =
        g.type === 'combo' && unset
          ? { ...it, kds: defaults.kds ?? null, printerIds: defaults.printerIds ? [...defaults.printerIds] : [] }
          : it;
      return seeded.childGroups.length
        ? { ...seeded, childGroups: applyInheritedDefaults(seeded.childGroups, defaults) }
        : seeded;
    }),
  }));
}

function fromModelGroup(g: VariantComboGroupView): UiGroup {
  const type: GroupType = g.groupType;
  const mode: ComboMode =
    g.pickLimit >= UNLIMITED_PICK_LIMIT
      ? 'unlimited'
      : g.minPick === 0
        ? 'upTo'
        : g.minPick === g.pickLimit
          ? 'exactly'
          : 'range';
  return {
    id: uid('g'),
    backendId: g.id,
    name: g.name,
    type,
    required: g.minPick >= 1,
    mode,
    nMin: g.minPick,
    nMax: g.pickLimit,
    freeOn: g.pickLimit > 1 && g.freeLimit > 0,
    freeLimit: g.freeLimit > 0 ? g.freeLimit : 1,
    items: g.items.map((it) => fromModelItem(it)),
  };
}

function fromModelItem(it: VariantComboItemView): UiItem {
  return {
    id: uid('it'),
    backendId: it.id,
    name: it.name,
    isDefault: it.isDefault ?? false,
    price: it.priceAfterFreeLimit,
    priceWithin: it.priceWithinFreeLimit,
    kds: it.kds,
    maxPick: it.maxPick ?? null,
    printerIds: it.printerIds ?? [],
    childGroups: (it.childGroups ?? []).map((cg) => fromModelGroup(cg)),
  };
}

export function toModel(
  g: UiGroup,
  parent: { productId?: number | null; itemId?: number | null },
  sortOrder: number,
): UpsertVariantComboGroupRequest {
  const { min, max } = pickLimits(g);
  const freeLimit = g.freeOn && max > 1 ? g.freeLimit : 0;
  const isNested = parent.itemId != null;
  return {
    id: g.backendId,
    groupType: g.type,
    parentProductId: isNested ? null : parent.productId ?? null,
    parentItemId: isNested ? parent.itemId ?? null : null,
    name: g.name.trim(),
    pickLimit: max,
    freeLimit,
    minPick: min,
    sortOrder,
    items: g.items.map((it, i) => ({
      id: it.backendId,
      name: it.name.trim(),
      priceWithinFreeLimit: freeLimit > 0 ? it.priceWithin : it.price,
      priceAfterFreeLimit: it.price,
      isDefault: it.isDefault,
      kds: g.type === 'combo' ? (it.kds ?? false) : null,
      maxPick: pickLimits(g).max > 1 ? it.maxPick : null,
      sortOrder: i,
      printerIds: g.type === 'combo' ? it.printerIds : [],
    })),
  };
}

export function toModelTree(g: UiGroup, sortOrder: number): GroupTreeNodeRequest {
  const { min, max } = pickLimits(g);
  const freeLimit = g.freeOn && max > 1 ? g.freeLimit : 0;
  return {
    id: g.backendId,
    groupType: g.type,
    name: g.name.trim(),
    pickLimit: max,
    freeLimit,
    minPick: min,
    sortOrder,
    items: g.items.map((it, i) => ({
      id: it.backendId,
      name: it.name.trim(),
      priceWithinFreeLimit: freeLimit > 0 ? it.priceWithin : it.price,
      priceAfterFreeLimit: it.price,
      isDefault: it.isDefault,
      kds: g.type === 'combo' ? (it.kds ?? false) : null,
      maxPick: pickLimits(g).max > 1 ? it.maxPick : null,
      sortOrder: i,
      printerIds: g.type === 'combo' ? it.printerIds : [],
      childGroups: it.childGroups.map((cg, j) => toModelTree(cg, j)),
    })),
  };
}

export function collectBackendGroupIds(groups: UiGroup[], acc: Set<number> = new Set()): Set<number> {
  for (const g of groups) {
    if (g.backendId != null) acc.add(g.backendId);
    for (const it of g.items) collectBackendGroupIds(it.childGroups, acc);
  }
  return acc;
}
